import { Container, Table, Badge, Button, Card, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faTrash,
    faPaste,
    faFilm,
    faBookmark,
    faCheckCircle,
    faExclamationCircle,
    faClock,
    faFolder,
    faThumbTack,
} from "@fortawesome/free-solid-svg-icons";
import { AppLayout } from "../components/appLayout";
import { useQuickAdd } from "../contexts/QuickAddContext";
import { getAddVideoBookmarkletHref } from "../components/forms/videoAddEditForm";
import { getAddBookmarkBookmarkletHref } from "../components/forms/bookmarkAddEditForm";

export const QuickAdd = () => {
    const { history, clearHistory } = useQuickAdd();

    return (
        <AppLayout loading={false}>
            <Container className="py-2">
                <Row className="align-items-center mb-3">
                    <Col>
                        <h1 className="mb-1">Quick Add</h1>
                        <p className="text-body-secondary mb-0">
                            Leave this page open and just tab here to paste links directly. A dialog will prompt you to classify them.
                        </p>
                    </Col>
                    {history.length > 0 && (
                        <Col xs="auto">
                            <Button variant="outline-danger" size="sm" onClick={clearHistory}>
                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                Clear History
                            </Button>
                        </Col>
                    )}
                </Row>

                <Card className="mb-4 border-dashed quick-add-banner">
                    <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-4">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary-subtle text-primary flex-shrink-0"
                                style={{ width: 48, height: 48 }}
                            >
                                <FontAwesomeIcon icon={faPaste} size="lg" />
                            </div>
                            <div>
                                <div className="fw-semibold">Ready for clipboard paste</div>
                                <div className="text-body-secondary small">
                                    Press <kbd>Ctrl+V</kbd> / <kbd>⌘V</kbd> anywhere on the page with one or more URLs copied to your clipboard.
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <span className="small text-body-secondary me-1">
                                <FontAwesomeIcon icon={faThumbTack} className="me-1" />
                                Bookmarklets (drag to bookmarks bar):
                            </span>
                            <a
                                ref={(el) => {
                                    if (el) el.setAttribute('href', getAddVideoBookmarkletHref());
                                }}
                                href="#bookmarklet-video"
                                onClick={(e) => e.preventDefault()}
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                title="Drag to your bookmarks bar to quickly add the current page as a Video"
                            >
                                <FontAwesomeIcon icon={faFilm} size="xs" />
                                <span>+ Add Video</span>
                            </a>
                            <a
                                ref={(el) => {
                                    if (el) el.setAttribute('href', getAddBookmarkBookmarkletHref());
                                }}
                                href="#bookmarklet-bookmark"
                                onClick={(e) => e.preventDefault()}
                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                                title="Drag to your bookmarks bar to quickly add the current page as a Bookmark"
                            >
                                <FontAwesomeIcon icon={faBookmark} size="xs" />
                                <span>+ Add Bookmark</span>
                            </a>
                        </div>
                    </Card.Body>
                </Card>

                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h2 className="h5 mb-0 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faBolt} className="text-primary" />
                        <span>Recent Adds ({history.length})</span>
                    </h2>
                </div>

                <Card>
                    <Table hover responsive className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th>URL</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center text-body-secondary py-5">
                                        No links pasted yet.
                                    </td>
                                </tr>
                            ) : (
                                history.map(item => (
                                    <tr key={item.id}>
                                        <td className="text-truncate" style={{ maxWidth: '360px' }} title={item.url}>
                                            <a href={item.url} target="_blank" rel="noreferrer" className="fw-medium">
                                                {item.url}
                                            </a>
                                        </td>
                                        <td>
                                            <Badge className="bg-secondary-subtle text-secondary-emphasis d-inline-flex align-items-center gap-1">
                                                <FontAwesomeIcon icon={item.type === 'video' ? faFilm : faBookmark} size="xs" />
                                                <span>{item.type === 'video' ? 'Video' : 'Bookmark'}</span>
                                            </Badge>
                                        </td>
                                        <td>
                                            <span className="text-body-secondary d-inline-flex align-items-center gap-1">
                                                <FontAwesomeIcon icon={faFolder} size="xs" />
                                                <span>{item.category}</span>
                                            </span>
                                        </td>
                                        <td>
                                            {item.status === 'pending' && (
                                                <Badge bg="secondary-subtle" className="text-secondary-emphasis d-inline-flex align-items-center gap-1">
                                                    <FontAwesomeIcon icon={faClock} size="xs" />
                                                    <span>Pending...</span>
                                                </Badge>
                                            )}
                                            {item.status === 'success' && (
                                                <Badge bg="success-subtle" className="text-success-emphasis d-inline-flex align-items-center gap-1">
                                                    <FontAwesomeIcon icon={faCheckCircle} size="xs" />
                                                    <span>Success</span>
                                                </Badge>
                                            )}
                                            {item.status === 'error' && (
                                                <div>
                                                    <Badge bg="danger-subtle" className="text-danger-emphasis d-inline-flex align-items-center gap-1" title={item.message}>
                                                        <FontAwesomeIcon icon={faExclamationCircle} size="xs" />
                                                        <span>Error</span>
                                                    </Badge>
                                                    <div className="text-danger small mt-1" style={{ wordBreak: 'break-word', maxWidth: '340px' }}>
                                                        {item.message}
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card>
            </Container>
        </AppLayout>
    );
};
